import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { t } from './i18n';

// ============================================================
// Drepturile contului de client (16 septembrie 2026, INTERFATA_CLIENT_PLAN.md, repriza 1).
//
// Pana acum bifele se puneau DOAR la crearea clientului; un client existent nu-si
// putea schimba drepturile de nicaieri. Fereastra asta se deschide din pagina
// clientului, doar pentru agentie (super_admin si manager; RLS pe `clienti` lasa
// doar pe ei sa scrie).
//
// „Vede ciornele" a iesit (decizia 1a): clientul vede toate postarile, pe tab-uri.
// „Poate publica" e noua (decizia 6c); e aparata si in baza (trigger) si in
// functiile de publicare, nu doar aici.
// ============================================================

export const GRUPE_DREPTURI = [
  { titlu: 'creareContinut', chei: ['poate_crea_postari', 'poate_publica', 'poate_folosi_ai_compositor'] },
  // „Vede programari" si „Vede social listening" au iesit (16 sept 2026): nu le citea
  // nimic din aplicatie, iar social listening n-a fost construit.
  { titlu: 'vizualizare', chei: ['vede_calendar', 'vede_biblioteca_media'] },
  // „Vede Temele zilei” (15 octombrie 2026, TEME_PLAN.md): bucataria agentiei, stinsa din oficiu,
  // pusa doar de ea; cu bifa, clientul vede tot ce e al lui (unghi, motiv, risc), dar nu alege.
  { titlu: 'statisticiMonitorizare', chei: ['vede_analytics_complet', 'vede_temele_zilei', 'vede_cercetarea'] },
  // „Bot la comentarii" (12 octombrie 2026): clientul isi pune, schimba, porneste si opreste
  // singur botul la postarile lui. Fara bifa, botul il poate porni doar super adminul.
  // E aparata si in baza: politica `bot_scenarii_scrie` (`bot_drept_client.sql`).
  // „Trimite mesaje abonaților" (14 octombrie 2026) e bifa separata, hotarata de ea: cine isi
  // porneste singur botul nu primeste automat si dreptul de a scrie tuturor abonatilor deodata.
  // „Poate descărca lista” (14 octombrie 2026, etapa 5): a treia bifa, tot doar a ei — „pentru
  // Prive e importanta lista”, la politicieni ramane goala.
  { titlu: 'inbox', chei: ['vede_inbox', 'raspunde_inbox', 'poate_bot_comentarii', 'poate_anunturi_abonati', 'poate_descarca_lista'] },
];

export const ETICHETE_DREPTURI = {
  poate_crea_postari: 'poateCreaPostari',
  poate_publica: 'poatePublica',
  poate_folosi_ai_compositor: 'poateFolosiAI',
  vede_calendar: 'vedeCalendar',
  vede_biblioteca_media: 'vedeBibliotecaMedia',
  vede_analytics_complet: 'vedeAnalytics',
  vede_inbox: 'vedeInbox',
  raspunde_inbox: 'raspundeMesaje',
  poate_bot_comentarii: 'poateBotComentarii',
  poate_anunturi_abonati: 'poateAnunturiAbonati',
  poate_descarca_lista: 'poateDescarcaLista',
  vede_temele_zilei: 'vedeTemeleZilei',
  vede_cercetarea: 'vedeCercetarea',
};

// Bifele pe care le pune DOAR super adminul (hotararea ei, 12 octombrie 2026: botul de
// comentarii il da deocamdata numai ea). Managerul nu le vede in fereastra, iar baza refuza
// schimbarea lor de catre altcineva (`bot_bifa_doar_super_admin.sql`).
export const CHEI_DOAR_SUPER_ADMIN = ['poate_bot_comentarii', 'poate_anunturi_abonati', 'poate_descarca_lista', 'vede_temele_zilei', 'vede_cercetarea'];

// Bifele care au sens doar impreuna cu alta.
const DEPINDE_DE = {
  poate_publica: 'poate_crea_postari',
  poate_folosi_ai_compositor: 'poate_crea_postari',
  raspunde_inbox: 'vede_inbox',
  poate_bot_comentarii: 'raspunde_inbox',
  poate_anunturi_abonati: 'raspunde_inbox',
  poate_descarca_lista: 'raspunde_inbox',
  // Cercetarea sta sub tema (18 octombrie 2026, CERCETARE_PLAN.md): fara teme n-are unde se vedea.
  vede_cercetarea: 'vede_temele_zilei',
};

export function comutaDrept(drepturi, cheie) {
  const nou = { ...drepturi, [cheie]: !drepturi[cheie] };
  if (!nou[cheie]) {
    Object.keys(DEPINDE_DE).forEach(k => { if (DEPINDE_DE[k] === cheie) nou[k] = false; });
  }
  return nou;
}

export function dreptBlocat(drepturi, cheie) {
  return !!DEPINDE_DE[cheie] && !drepturi[DEPINDE_DE[cheie]];
}

export default function DrepturiClient({ client, onInchide, onSalvat }) {
  const [drepturi, setDrepturi] = useState(() => {
    const d = { ...(client.permisiuni_client || {}) };
    delete d.vede_drafturi;
    delete d.vede_programari;
    delete d.vede_social_listening;
    return d;
  });
  const [seSalveaza, setSeSalveaza] = useState(false);
  const [eroare, setEroare] = useState('');
  // Fereastra nu primeste rolul celui care o deschide, asa ca intreaba baza — aceeasi functie
  // pe care se sprijina si regulile de acces.
  const [eSuperAdmin, setESuperAdmin] = useState(false);
  useEffect(() => {
    let anulat = false;
    supabase.rpc('este_super_admin').then(({ data }) => { if (!anulat) setESuperAdmin(data === true); });
    return () => { anulat = true; };
  }, []);

  async function salveaza() {
    setSeSalveaza(true);
    setEroare('');
    const { data, error } = await supabase
      .from('clienti')
      .update({ permisiuni_client: drepturi })
      .eq('id', client.id)
      .select('id, permisiuni_client');
    setSeSalveaza(false);
    if (error || !data || !data.length) {
      setEroare(error ? error.message : t('clienti', 'drepturiNesalvate'));
      return;
    }
    if (onSalvat) onSalvat(data[0].permisiuni_client);
    onInchide();
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2 className="modal-titlu">{t('clienti', 'drepturiTitlu', { nume: client.nume })}</h2>
        <p style={{ fontSize: 13, color: '#374151', marginTop: 0 }}>{t('clienti', 'drepturiExplicatie')}</p>
        {GRUPE_DREPTURI.map(grupa => (
          <div key={grupa.titlu} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#1A6B3C', marginBottom: 10 }}>
              {t('clienti', grupa.titlu)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 4 }}>
              {grupa.chei.filter(cheie => eSuperAdmin || !CHEI_DOAR_SUPER_ADMIN.includes(cheie)).map(cheie => {
                const blocat = dreptBlocat(drepturi, cheie);
                return (
                  <label key={cheie} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#1F2937', cursor: blocat ? 'not-allowed' : 'pointer', opacity: blocat ? 0.5 : 1 }}>
                    <input
                      type="checkbox"
                      checked={!!drepturi[cheie]}
                      disabled={blocat || seSalveaza}
                      onChange={() => setDrepturi(prev => comutaDrept(prev, cheie))}
                    />
                    {t('clienti', ETICHETE_DREPTURI[cheie])}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        {eroare && <p style={{ color: '#B91C1C', fontSize: 13 }}>{eroare}</p>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secundar" onClick={onInchide} disabled={seSalveaza}>{t('clienti', 'anuleaza')}</button>
          <button type="button" className="btn-principal" onClick={salveaza} disabled={seSalveaza}>
            {seSalveaza ? t('clienti', 'drepturiSeSalveaza') : t('clienti', 'drepturiSalveaza')}
          </button>
        </div>
      </div>
    </div>
  );
}
